import React, { useState, useEffect } from 'react';
import { FiFileText, FiEye, FiCheckCircle, FiXCircle, FiClock, FiX, FiAlertCircle } from 'react-icons/fi';
import api from '../../utils/api';

const font = "'Outfit', sans-serif";
const C = { text: '#1e293b', sub: '#64748b', border: '#e2e8f0', bg: '#f8fafc', primary: '#10b981', orange: '#f59e0b', red: '#ef4444' };

const getStatusStyle = (status) => {
    if (status === 'approved') return { bg: '#ecfdf5', color: C.primary, icon: <FiCheckCircle />, label: 'Approved' };
    if (status === 'rejected') return { bg: '#fef2f2', color: C.red, icon: <FiXCircle />, label: 'Rejected' };
    if (status === 'draft') return { bg: '#f1f5f9', color: '#94a3b8', icon: <FiFileText />, label: 'Draft' };
    return { bg: '#fffbeb', color: C.orange, icon: <FiClock />, label: 'Submitted' };
};

const AdminReviewPapers = () => {
    const [papers, setPapers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [previewPaper, setPreviewPaper] = useState(null);
    const [previewQuestions, setPreviewQuestions] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [search, setSearch] = useState('');
    const [errorModal, setErrorModal] = useState({ show: false, message: '' });

    useEffect(() => { fetchPapers(); }, []);

    const fetchPapers = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/admin/papers');
            setPapers(res.data.papers || []);
        } catch (err) {
            console.error('Error fetching papers:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = async (id) => {
        setPreviewLoading(true);
        try {
            const res = await api.get(`/api/admin/papers/${id}`);
            setPreviewPaper(res.data.paper);
            setPreviewQuestions(res.data.questions || []);
        } catch (err) {
            console.error(err);
            if (err.response?.status === 403) {
                setErrorModal({ show: true, message: err.response.data.message });
            } else {
                alert(err.response?.data?.message || 'Failed to load paper preview.');
            }
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleReview = async (paperId, action) => {
        const label = action === 'approved' ? 'approve' : 'reject';
        let admin_remarks = '';
        if (action === 'rejected') {
            admin_remarks = prompt('Please provide a reason for rejection (optional):') || '';
        }
        if (!window.confirm(`Are you sure you want to ${label} this paper?`)) return;

        setActionLoading(paperId);
        try {
            await api.put(`/api/admin/papers/${paperId}/review`, { action, admin_remarks });
            fetchPapers();
            if (previewPaper && previewPaper.id === paperId) {
                setPreviewPaper({ ...previewPaper, status: action, admin_remarks });
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Error reviewing paper');
        } finally {
            setActionLoading(null);
        }
    };

    const filtered = papers.filter(p => {
        const matchesFilter = filter === 'all' || p.status === filter;
        const q = search.toLowerCase();
        const matchesSearch = !search || 
            p.title?.toLowerCase().includes(q) || 
            p.subject_name?.toLowerCase().includes(q) || 
            p.subject_code?.toLowerCase().includes(q) || 
            (p.teacher_name || p.teacher_username)?.toLowerCase().includes(q);
        return matchesFilter && matchesSearch;
    });
    const counts = {
        all: papers.length,
        submitted: papers.filter(p => p.status === 'submitted').length,
        approved: papers.filter(p => p.status === 'approved').length,
        rejected: papers.filter(p => p.status === 'rejected').length,
    };

    return (
        <div style={{ padding: '0px 10px', fontFamily: font, maxWidth: 1200, margin: '0 auto', animation: 'fadeIn 0.4s ease' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FiFileText color={C.primary} /> Review Exam Papers
                    </h1>
                    <p style={{ margin: 0, color: C.sub, fontSize: 14 }}>Examine and approve question papers submitted by assigned teachers.</p>
                </div>
                <div style={{ position: 'relative', width: '100%', maxWidth: 350 }}>
                    <input 
                        type="text" 
                        placeholder="Search title, subject, teacher..." 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ 
                            width: '100%', padding: '10px 16px', paddingRight: 40, borderRadius: 12, 
                            border: `1.5px solid ${C.border}`, fontSize: 14, fontFamily: font, 
                            outline: 'none', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                            transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = C.primary}
                        onBlur={(e) => e.target.style.borderColor = C.border}
                    />
                    {search && (
                        <button 
                            onClick={() => setSearch('')} 
                            style={{ 
                                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', 
                                background: 'none', border: 'none', color: C.sub, cursor: 'pointer', 
                                fontSize: 18, fontWeight: 800 
                            }}
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>

            {/* Filter Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
                {[
                    { key: 'all', label: 'All' },
                    { key: 'submitted', label: 'Pending Review' },
                    { key: 'approved', label: 'Approved' },
                    { key: 'rejected', label: 'Rejected' },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setFilter(tab.key)}
                        style={{
                            padding: '8px 16px', borderRadius: 20, border: `1px solid ${filter === tab.key ? C.primary : C.border}`,
                            background: filter === tab.key ? '#ecfdf5' : '#fff', color: filter === tab.key ? C.primary : C.sub,
                            fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: font, transition: 'all 0.2s'
                        }}
                    >
                        {tab.label} ({counts[tab.key]})
                    </button>
                ))}
            </div>

            {/* Papers Grid */}
            {loading ? (
                <div style={{ padding: 60, textAlign: 'center', color: C.sub, fontSize: 15 }}>Loading papers...</div>
            ) : filtered.length === 0 ? (
                <div style={{ padding: 60, textAlign: 'center', color: C.sub }}>
                    <FiAlertCircle size={32} style={{ marginBottom: 12, opacity: 0.5 }} />
                    <div style={{ fontSize: 15 }}>No papers found{filter !== 'all' ? ` with status "${filter}"` : ''}.</div>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
                    {filtered.map(p => {
                        const ss = getStatusStyle(p.status);
                        return (
                            <div key={p.id} style={{
                                background: '#fff', borderRadius: 16, border: `1px solid ${C.border}`,
                                padding: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                                display: 'flex', flexDirection: 'column', gap: 14, transition: 'box-shadow 0.2s'
                            }}>
                                {/* Header */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                                    <div style={{ minWidth: 0 }}>
                                        <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</h3>
                                        <div style={{ fontSize: 13, color: '#6366f1', fontWeight: 600 }}>{p.subject_code}</div>
                                    </div>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
                                        padding: '4px 10px', borderRadius: 20, background: ss.bg, color: ss.color,
                                        fontSize: 12, fontWeight: 700, textTransform: 'capitalize'
                                    }}>
                                        {ss.icon} {ss.label}
                                    </div>
                                </div>

                                {/* Details */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: C.sub }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Subject:</span>
                                        <span style={{ fontWeight: 600, color: C.text, textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.subject_name}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Teacher:</span>
                                        <span style={{ fontWeight: 600, color: C.text }}>{p.teacher_name || p.created_by || p.teacher_username}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Batch:</span>
                                        <span style={{ fontWeight: 600, color: C.text }}>{p.batch_name}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Questions:</span>
                                        <span style={{ fontWeight: 600, color: C.text }}>{p.question_count} &middot; {p.total_marks} marks &middot; {p.duration_mins} mins</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Submitted:</span>
                                        <span style={{ fontWeight: 600, color: C.text }}>{new Date(p.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                                    </div>
                                    {p.admin_remarks && (
                                        <div style={{ marginTop: 4, padding: 8, background: p.status === 'rejected' ? '#fef2f2' : '#ecfdf5', borderRadius: 6, fontSize: 12, color: p.status === 'rejected' ? '#b91c1c' : '#047857' }}>
                                            <strong>Remarks:</strong> {p.admin_remarks}
                                        </div>
                                    )}
                                </div>

                                {/* Actions */}
                                <div style={{ marginTop: 'auto', display: 'flex', gap: 8, borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
                                    <button
                                        onClick={() => handlePreview(p.id)}
                                        style={{ flex: 1, padding: 10, borderRadius: 8, background: C.bg, color: C.text, border: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontWeight: 600, fontFamily: font, fontSize: 13, transition: 'background 0.2s' }}
                                    >
                                        <FiEye size={16} /> Preview
                                    </button>
                                    {p.status === 'submitted' && (
                                        <>
                                            <button
                                                onClick={() => handleReview(p.id, 'approved')}
                                                disabled={actionLoading === p.id}
                                                style={{ padding: '10px 14px', borderRadius: 8, background: '#ecfdf5', color: C.primary, border: '1px solid #a7f3d0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, transition: 'background 0.2s', fontSize: 13, fontFamily: font, gap: 4 }}
                                            >
                                                <FiCheckCircle size={16} /> Approve
                                            </button>
                                            <button
                                                onClick={() => handleReview(p.id, 'rejected')}
                                                disabled={actionLoading === p.id}
                                                style={{ padding: '10px 14px', borderRadius: 8, background: '#fef2f2', color: C.red, border: '1px solid #fecaca', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, transition: 'background 0.2s', fontSize: 13, fontFamily: font, gap: 4 }}
                                            >
                                                <FiXCircle size={16} /> Reject
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Preview Modal */}
            {(previewPaper || previewLoading) && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 20, animation: 'fadeIn 0.2s ease'
                }} onClick={() => { setPreviewPaper(null); setPreviewQuestions([]); }}>
                    <div
                        style={{
                            background: '#fff', borderRadius: 20, width: '100%', maxWidth: 800,
                            maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.15)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {previewLoading ? (
                            <div style={{ padding: 60, textAlign: 'center', color: C.sub }}>Loading paper...</div>
                        ) : previewPaper && (
                            <>
                                {/* Modal Header */}
                                <div style={{
                                    padding: '20px 24px', borderBottom: `1px solid ${C.border}`,
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderRadius: '20px 20px 0 0'
                                }}>
                                    <div>
                                        <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: C.text }}>{previewPaper.title}</h2>
                                        <div style={{ fontSize: 13, color: C.sub }}>
                                            {previewPaper.subject_code} &middot; {previewPaper.subject_name} &middot; {previewPaper.batch_name}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => { setPreviewPaper(null); setPreviewQuestions([]); }}
                                        style={{ padding: 8, borderRadius: 8, background: C.bg, border: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', color: C.sub }}
                                    >
                                        <FiX size={18} />
                                    </button>
                                </div>

                                {/* Paper Info */}
                                <div style={{ padding: '16px 24px', background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, fontSize: 13 }}>
                                        <div><span style={{ color: C.sub }}>Teacher:</span> <strong>{previewPaper.teacher_name || previewPaper.created_by || previewPaper.teacher_username}</strong></div>
                                        <div><span style={{ color: C.sub }}>Department:</span> <strong>{previewPaper.department}</strong></div>
                                        <div><span style={{ color: C.sub }}>Duration:</span> <strong>{previewPaper.duration_mins} mins</strong></div>
                                        <div><span style={{ color: C.sub }}>Total Marks:</span> <strong>{previewPaper.total_marks}</strong></div>
                                        <div><span style={{ color: C.sub }}>Exam Type:</span> <strong style={{ textTransform: 'capitalize' }}>{previewPaper.exam_type}</strong></div>
                                        <div><span style={{ color: C.sub }}>Status:</span> <strong style={{ textTransform: 'capitalize', color: getStatusStyle(previewPaper.status).color }}>{previewPaper.status}</strong></div>
                                    </div>
                                    {previewPaper.instructions && (
                                        <div style={{ marginTop: 12, padding: 10, background: '#fff', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, color: C.text }}>
                                            <strong>Instructions:</strong> {previewPaper.instructions}
                                        </div>
                                    )}
                                </div>

                                {/* Questions */}
                                <div style={{ padding: '20px 24px' }}>
                                    <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 16px', color: C.text }}>
                                        Questions ({previewQuestions.length})
                                    </h3>
                                    {previewQuestions.length === 0 ? (
                                        <div style={{ padding: 30, textAlign: 'center', color: C.sub, background: C.bg, borderRadius: 12 }}>
                                            No questions added to this paper yet.
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                            {previewQuestions.map((q, idx) => (
                                                <div key={q.id} style={{
                                                    padding: 16, background: C.bg, borderRadius: 12,
                                                    border: `1px solid ${C.border}`
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                                        <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Q{q.question_no || idx + 1}.</span>
                                                        <span style={{ fontSize: 12, color: C.sub, fontWeight: 600 }}>{q.marks} marks &middot; {q.question_type}</span>
                                                    </div>
                                                    <p style={{ margin: '0 0 10px', fontSize: 14, color: C.text, lineHeight: 1.6 }}>{q.question_text}</p>
                                                    {q.image_url && (
                                                        <div style={{ marginBottom: 12, borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}`, background: '#fff' }}>
                                                            <img 
                                                                src={`${api.defaults.baseURL}${q.image_url}`} 
                                                                alt="Question" 
                                                                style={{ maxWidth: '100%', maxHeight: 300, display: 'block', margin: '0 auto', cursor: 'zoom-in' }} 
                                                                onClick={(e) => { e.stopPropagation(); window.open(`${api.defaults.baseURL}${q.image_url}`, '_blank'); }}
                                                            />
                                                        </div>
                                                    )}
                                                    {q.question_type === 'mcq' && (q.option_a || q.option_b || q.option_c || q.option_d) && (
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                                            {['A', 'B', 'C', 'D'].map(opt => {
                                                                const val = q[`option_${opt.toLowerCase()}`];
                                                                if (!val) return null;
                                                                const isCorrect = q.correct_option && q.correct_option.toUpperCase() === opt;
                                                                return (
                                                                    <div key={opt} style={{
                                                                        padding: '8px 12px', borderRadius: 8, fontSize: 13,
                                                                        background: isCorrect ? '#ecfdf5' : '#fff',
                                                                        border: `1px solid ${isCorrect ? '#a7f3d0' : C.border}`,
                                                                        color: isCorrect ? '#047857' : C.text,
                                                                        fontWeight: isCorrect ? 600 : 400
                                                                    }}>
                                                                        <span style={{ fontWeight: 700 }}>{opt}.</span> {val}
                                                                        {isCorrect && <span style={{ marginLeft: 6, fontSize: 11 }}>✓</span>}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Modal Footer — Approve/Reject for submitted papers */}
                                {previewPaper.status === 'submitted' && (
                                    <div style={{
                                        padding: '16px 24px', borderTop: `1px solid ${C.border}`,
                                        display: 'flex', gap: 12, justifyContent: 'flex-end',
                                        position: 'sticky', bottom: 0, background: '#fff', borderRadius: '0 0 20px 20px'
                                    }}>
                                        <button
                                            onClick={() => handleReview(previewPaper.id, 'rejected')}
                                            style={{ padding: '10px 20px', borderRadius: 8, background: '#fef2f2', color: C.red, border: '1px solid #fecaca', cursor: 'pointer', fontWeight: 700, fontFamily: font, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}
                                        >
                                            <FiXCircle size={16} /> Reject
                                        </button>
                                        <button
                                            onClick={() => handleReview(previewPaper.id, 'approved')}
                                            style={{ padding: '10px 20px', borderRadius: 8, background: C.primary, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontFamily: font, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}
                                        >
                                            <FiCheckCircle size={16} /> Approve
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            {/* Error Modal */}
            {errorModal.show && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', zIndex: 2000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 20, animation: 'fadeIn 0.2s ease'
                }} onClick={() => setErrorModal({ show: false, message: '' })}>
                    <div
                        style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 400, padding: 24, textAlign: 'center', boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <FiClock size={32} />
                        </div>
                        <h3 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800, color: C.text }}>Exam Locked</h3>
                        <p style={{ margin: '0 0 24px', fontSize: 14, color: C.sub, lineHeight: 1.5 }}>{errorModal.message}</p>
                        <button
                            onClick={() => setErrorModal({ show: false, message: '' })}
                            style={{ width: '100%', padding: '12px', borderRadius: 12, background: C.bg, color: C.text, border: `1px solid ${C.border}`, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: font }}
                        >
                            Understood
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default AdminReviewPapers;
